import React, { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import SidebarModal from "@surgia/ui/components/Modals/SidebarModal";
import Button from "@surgia/ui/components/button";
import Badge from "@surgia/ui/components/badge";
import QuantityControls from "../../components/quantityControls";
import { useUpdatePurchaseRequest } from "../../hooks/useUpdatePurchaseRequest";
import { useDeletePurchaseRequestItem } from "../../hooks/useDeletePurchaseRequestItem";

// Icons
import closeIcon from "@surgia/ui/icons/popup/close.svg";
import hoveredShoppingCartIcon from "@surgia/ui/icons/catalog/hub/shoppingCartWhite.svg";
import brandIcon from "@surgia/ui/icons/catalog/brand.svg";

import { ProductVariationModalProps } from "./constant";
import { catalogMutations } from "@surgia/api/src/queryStore/hub/catalog";
import { useQuery } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { permissions } from "../../permissions";
import { getLocalStorageItem } from "../../pages/Catalog";
import config from "@surgia/config";

const ProductVariationModal: React.FC<ProductVariationModalProps> = ({
  open,
  onClose,
  product,
  tenant_id,
  setSelectedProductId,
  selectedProductId,
  draftPurchaseRequestID,
}) => {
  interface OutletContextType {
    branchesDropdown: any[];
    selectedBranch: string | null;
    setSelectedBranch: (branch: string | null) => void;
  }
  const userPermissions = getLocalStorageItem("permissions", []);

  const { selectedBranch } = useOutletContext<OutletContextType>();
  const selectedBranchId =
    selectedBranch && selectedBranch?.includes(",")
      ? selectedBranch.split(",")[0]
      : selectedBranch;

  const deletePurchaseRequestItemMutation = useDeletePurchaseRequestItem({
    branchId: selectedBranchId || "",
    componentId: String(config.componentId),
    tenantId: tenant_id || "",
  });

  const { data: variationMapsData, isLoading: isLoadingVariationMaps } =
    useQuery({
      ...catalogMutations.queries.getProductVariationMaps({
        productId: selectedProductId,
        tenantId: tenant_id || "",
        branchId: selectedBranchId || "",
        componentId: String(config.componentId),
      }),
      enabled: !!product?.id && !!tenant_id && !!selectedBranch,
    });

  const currentCountInBranchPR = useMemo(() => {
    if (!variationMapsData || !selectedProductId) return 0;

    const matchingVariation = variationMapsData[0].variations.find(
      (variation) =>
        variation?.tenant_product?.id?.toString() == selectedProductId
    );

    if (matchingVariation) {
      const count = matchingVariation.tenant_product.count_in_branch_pr || 0;
      return count;
    }

    return 0;
  }, [variationMapsData, selectedProductId]);
  const [quantity, setQuantity] = useState(0);
  const [inputValue, setInputValue] = useState("0");
  const selectedVariation = useMemo(() => {
    if (!variationMapsData || !selectedProductId) return null;

    return variationMapsData
      .flatMap((feature) => feature.variations)
      .find(
        (variation) =>
          variation?.tenant_product?.id?.toString() ===
          selectedProductId?.toString()
      );
  }, [variationMapsData, selectedProductId]);
  useEffect(() => {
    setQuantity(currentCountInBranchPR);
    setInputValue(
      currentCountInBranchPR
        ? currentCountInBranchPR.toString()
        : selectedVariation?.tenant_product?.minimum_quantity?.toString() || "0"
    );
  }, [currentCountInBranchPR]);

  const updatePurchaseRequestMutation = useUpdatePurchaseRequest({
    branchId: selectedBranchId || "",
    componentId: String(config.componentId),
    tenantId: tenant_id || "",
    draftPurchaseRequestID: draftPurchaseRequestID
      ? draftPurchaseRequestID?.toString()
      : "",
    minQuantity: selectedVariation?.tenant_product?.minimum_quantity,
    maxQuantity: selectedVariation?.tenant_product?.maximum_quantity,
  });
  const handleAttributeChange = (productId: string) => {
    setSelectedProductId(productId);
  };

  const handleQuantityChange = async (newQuantity: number) => {
    if (selectedProductId) {
      await updatePurchaseRequestMutation.mutateAsync({
        branchId: selectedBranchId!,
        componentId: String(config.componentId),
        tenantId: tenant_id || "",
        body: {
          tenant_product_id: selectedProductId,
          quantity: newQuantity,
        },
      });
      setQuantity(newQuantity);
    }
  };

  const handleAddToPR = () => {
    // Validate quantity before adding to PR
    if (quantity < (selectedVariation?.tenant_product?.minimum_quantity || 0)) {
      toast.error(
        `Quantity cannot be less than ${selectedVariation?.tenant_product?.minimum_quantity}`
      );
      return;
    }

    if (
      quantity > (selectedVariation?.tenant_product?.maximum_quantity || 999999)
    ) {
      toast.error(
        `Quantity cannot be more than ${selectedVariation?.tenant_product?.maximum_quantity}`
      );
      return;
    }

    handleQuantityChange(quantity);
  };
  // Check if any mutation is pending
  const isUpdating = updatePurchaseRequestMutation.isPending;
  const isDeleting = deletePurchaseRequestItemMutation.isPending;

  return (
    <SidebarModal open={open} onClose={onClose}>
      <div className="flex flex-col h-full">
        {/* Header - Fixed */}
        <div className="flex items-start justify-between p-4 pb-0 flex-shrink-0">
          <div className="flex-1">
            <div className="flex flex-wrap gap-2">
              {product.category.map((category) => (
                <Badge
                  key={category}
                  variant="default"
                  size="sm"
                  className="bg-gray-100 text-gray-700 border-gray-200 mb-3"
                >
                  {category}
                </Badge>
              ))}
            </div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">
              {product.name}
            </h2>
          </div>
          <button onClick={onClose} className="transition-colors ml-4">
            <img src={closeIcon} alt="Close" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Product Image */}
          <div className="p-4">
            <div className="w-full h-64 bg-gray-100 rounded-sm overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Brand Name */}
          <div className="px-4 mb-6">
            <div className="flex items-center gap-2">
              {product.brand.logo ? (
                <img
                  src={brandIcon}
                  alt={product.brand.name}
                  className="rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">
                    {product.brand.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-[16px] text-[#181D27] font-bold">
                {product.brand.name}
              </span>
            </div>
          </div>

          {/* Product Attributes */}
          <div className="px-4 space-y-6">
            {isLoadingVariationMaps ? (
              // Skeleton for loading state
              <>
                {/* Min/Max quantity skeleton */}
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-32 mb-6"></div>
                </div>

                {/* Feature sections skeleton */}
                {[1, 2, 3].map((index) => (
                  <div key={index} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3].map((btnIndex) => (
                        <div
                          key={btnIndex}
                          className="h-10 bg-gray-200 rounded w-20"
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              // Actual content
              <>
                {selectedVariation?.tenant_product?.maximum_quantity &&
                  selectedVariation?.tenant_product?.minimum_quantity && (
                    <p className="text-sm">
                      min: {selectedVariation?.tenant_product?.minimum_quantity}{" "}
                      - max:{" "}
                      {selectedVariation?.tenant_product?.maximum_quantity}
                    </p>
                  )}

                {variationMapsData?.map((feature) => {
                  return (
                    <div key={feature.feature.id}>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">
                        {feature.feature.name}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {feature.variations.map((variation) => (
                          <Button
                            key={variation.feature_variant.variant_id}
                            variant={
                              variation?.tenant_product?.id ===
                              +selectedProductId
                                ? "variantSelected"
                                : "variantDefault"
                            }
                            onClick={() =>
                              handleAttributeChange(
                                variation?.tenant_product?.id.toString()
                              )
                            }
                            className="!h-10 flex items-center justify-center"
                            disabled={isUpdating || isDeleting}
                          >
                            {variation.feature_variant.variant_name}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {/* Price - Fixed */}
        {userPermissions?.includes(permissions.VIEW_PRODUCT_PRICE) && (
          <div className="px-4 bg-transparent mt-4 p-2 flex-shrink-0 ">
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-gray-900">
                {product.price.amount.toLocaleString()}{" "}
                <span className="text-sm text-gray-400 font-medium ml-1">
                  {product.price.currency}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* Footer - Fixed */}
        <div className="py-6 px-4 pt-0 flex-shrink-0">
          <div className="flex items-center gap-2">
            <QuantityControls
              quantity={quantity}
              onQuantityChange={handleQuantityChange}
              onRemove={() =>
                setQuantity(
                  selectedVariation?.tenant_product?.minimum_quantity!
                )
              }
              minQuantity={selectedVariation?.tenant_product?.minimum_quantity!}
              maxQuantity={selectedVariation?.tenant_product?.maximum_quantity!}
              showDeleteButton={false}
              setQuantity={setQuantity}
              inputValue={inputValue}
              setInputValue={setInputValue}
            />

            <Button
              variant="purple"
              onClick={handleAddToPR}
              startIcon={hoveredShoppingCartIcon}
              className="flex-1 !bg-[#7144FF] hover:bg-purple-700 text-white"
              disabled={
                isUpdating ||
                isDeleting ||
                +inputValue == currentCountInBranchPR ||
                +inputValue >
                  +selectedVariation?.tenant_product?.maximum_quantity! ||
                +inputValue <
                  +selectedVariation?.tenant_product?.minimum_quantity!
              }
            >
              Add to PR
            </Button>
          </div>
        </div>
      </div>
    </SidebarModal>
  );
};

export default ProductVariationModal;
